import type { OptionsDocument, QuoteDocument } from 'paintscout'
import { isEmpty as isHtmlEmpty } from '../../../../util/isEmpty'
import { isSignatureRequired } from '../../../options/is-signature-required'

/**
 * True when the terms section has nothing worth rendering, so every read-only surface can drop it.
 *
 * Three things keep the section alive:
 *
 *  1. Terms text. `content` is HTML, and "blank" terms are rarely the empty string — the editor
 *     leaves behind `<p></p><p></p>`, `<p><br></p>` or `&nbsp;`. Strip the tags and test what is
 *     left, the same way `DocumentsIsEmpty` and `PresentationAcceptButton` do. A single
 *     `.replace('<p></p>', '')` only removed the *first* empty paragraph, so two of them read as
 *     content and the customer view drew an empty bordered box (ENG-1494).
 *  2. A signature already collected on an accepted quote.
 *  3. A signature the quote still needs. `QuoteSignCard` renders *inside* `QuoteTerms`, so dropping
 *     the section on a quote that requires a signature leaves the customer no way to accept — and
 *     silently, since it only happens on their copy and the PDF (ENG-1510). `isEmpty` cannot tell
 *     the customer view from the estimator's preview, so either requirement keeps the section. An
 *     empty bordered box is a cosmetic bug; a missing signature box blocks a sale.
 *
 * Neither signature case applies to an invoice: `QuoteSignCard` returns null for every invoice, so a
 * text-less invoice kept for its signature drew an empty box alone on the last printed page, after
 * the quote section's forced page break (ENG-2028).
 *
 * Used directly as the `isEmpty` entry of both Terms registries — `presentation/sections/Terms` (v1)
 * and `PresentationNext/blocks/Terms` (v2) — which previously held identical copies of this logic.
 * The registries call it with the full presentation props; only `quote` and `options` are read.
 */
export function isTermsEmpty({ quote, options }: { quote?: QuoteDocument; options?: OptionsDocument }): boolean {
  const hasContent = !!quote?.terms?.content && !isHtmlEmpty(quote.terms.content)
  if (hasContent) {
    return false
  }

  if (quote?.is_invoice) {
    return true
  }

  const isAccepted = quote?.status?.value === 'accepted'
  const hasSignature = !!quote?.signatures?.customer
  if (isAccepted && hasSignature) {
    return false
  }

  // No options means no way to read the signature settings (the presentation settings preview calls
  // isEmpty without them, and so do the presentation specs). Treat that as "not required" and fall
  // back to the text test alone. `options.options` is checked too, not just `options`:
  // `isSignatureRequired` reads straight through it and throws on a partial document.
  if (!options?.options || !quote) {
    return true
  }

  const needsSignature =
    !!isSignatureRequired({ options, quote, path: 'requireSignatureSent' }) ||
    !!isSignatureRequired({ options, quote, path: 'requireSignatureOnSpot' })

  return !needsSignature
}
